import type { TFunction } from 'i18next';

/**
 * Rechazos del paquete (feature-106 F7): ninguno se reintenta solo, porque el
 * resultado sería el mismo hasta que cambie el paquete o empiece el ciclo.
 */
export type PackageErrorCode = 'FEATURE_QUOTA_EXCEEDED' | 'FEATURE_NOT_IN_PACKAGE';

const CODES: ReadonlySet<string> = new Set<PackageErrorCode>(['FEATURE_QUOTA_EXCEEDED', 'FEATURE_NOT_IN_PACKAGE']);
const quotaListeners = new Set<() => void>();

function codeOf(value: unknown): unknown {
  if (!value || typeof value !== 'object') return undefined;
  const v = value as { code?: unknown; error?: { code?: unknown }; response?: { data?: { error?: { code?: unknown } } } };
  return v.response?.data?.error?.code ?? v.error?.code ?? v.code;
}

/**
 * Código de paquete de un error (`ApiError`, error de axios o cuerpo JSON del backend).
 * Con la cuota agotada avisa a `useEntitlements` para que refresque `quotaExceeded`.
 */
export function packageErrorCode(error: unknown): PackageErrorCode | null {
  const code = codeOf(error);
  if (typeof code !== 'string' || !CODES.has(code)) return null;
  if (code === 'FEATURE_QUOTA_EXCEEDED') quotaListeners.forEach((listener) => listener());
  return code as PackageErrorCode;
}

/** Código de paquete de una respuesta `fetch` fallida (SSE, multipart…); no consume el cuerpo original. */
export async function packageErrorOfResponse(response: Response): Promise<PackageErrorCode | null> {
  if (response.status !== 403) return null;
  try {
    return packageErrorCode(await response.clone().json());
  } catch {
    return null;
  }
}

/** Error que conserva el código para los servicios que lanzan `Error` a partir de `fetch`. */
export class PackageError extends Error {
  readonly code: PackageErrorCode;

  constructor(code: PackageErrorCode) {
    super(code);
    this.name = 'PackageError';
    this.code = code;
  }
}

export function packageErrorMessage(t: TFunction, code: PackageErrorCode): string {
  return code === 'FEATURE_QUOTA_EXCEEDED'
    ? t('quota.exceeded', 'Créditos agotados, contacta con tu administrador')
    : t('quota.notInPackage', 'Esta funcionalidad no está incluida en el paquete de tu institución');
}

/** `useEntitlements` se suscribe para volver a pedir `/entitlements/me`. */
export function onQuotaExceeded(listener: () => void): () => void {
  quotaListeners.add(listener);
  return () => { quotaListeners.delete(listener); };
}
